'use client';

import { useDialog } from '@react-aria/dialog';
import { FocusScope } from '@react-aria/focus';
import { useModal, useOverlay, usePreventScroll } from '@react-aria/overlays';
import type { ReactNode } from 'react';
import React from 'react';
import styled from 'styled-components';

import { Breakpoints, ModalMaxWidth } from '../../theme/breakpoints';
import { cn } from '../../utils/cva';

type Props = {
  children?: ReactNode;
  isOpen?: boolean;
  isDismissable?: boolean;
  onClose?: () => void;
  containerClassName?: string;
  rootClassName?: string;
} & ModalSize;

type ModalSize = {
  size?: 'sm' | 'md' | 'lg' | 'xl';
};

export const Modal = (props: Props) => {
  const { size = 'sm', isDismissable = true, children, containerClassName, rootClassName } = props;
  const ref = React.useRef<HTMLDivElement>(null);
  const { overlayProps, underlayProps } = useOverlay(
    { isOpen: props.isOpen, onClose: props.onClose, isDismissable: isDismissable },
    ref,
  );

  // Prevent scrolling while the modal is open, and hide content
  // outside the modal from screen readers.
  usePreventScroll();
  const { modalProps } = useModal();

  // Get props for the dialog and its title
  const { dialogProps } = useDialog({}, ref);

  return (
    <ModalRoot {...underlayProps} className={cn(underlayProps.className, rootClassName)}>
      <FocusScope contain restoreFocus autoFocus>
        <ModalContainer
          size={size}
          {...overlayProps}
          {...dialogProps}
          {...modalProps}
          className={cn(overlayProps.className, containerClassName)}
          ref={ref}
        >
          {children}
        </ModalContainer>
      </FocusScope>
    </ModalRoot>
  );
};

const ModalRoot = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  background-color: #19192350;
  backdrop-filter: blur(5px);
  width: 100%;
  height: 100%;
  z-index: 150;
  overflow: auto;
`;

const ModalContainer = styled.div<ModalSize>`
  position: fixed;
  margin: auto;
  box-shadow: 0 5px 30px 0 rgba(25, 25, 35, 0.05);
  ${({ size }) => size && size !== 'xl' && `max-width: ${ModalMaxWidth[size]}`};

  width: 100%;
  @media (min-width: ${Breakpoints.md}) {
    width: calc(100% - 6.4rem);
  }
`;
